import type {
  EventAttendeesResponse,
  EventPantryItem,
  Meal,
  MealsResponse,
  PantryKind,
} from '@sage-burner/shared'

import {
  bringPage,
  byWanted,
  dayName,
  MAX_OPTION_LABEL,
  MAX_WELCOME_LENGTH,
  pantryPage,
  schedulePage,
  shoppingPage,
} from '@sage-burner/shared'
import { useState } from 'preact/hooks'

import type { ApiClient } from '../api/client.ts'
import type { UploadImage } from '../image-upload.ts'

import { useSelectedBurn } from '../burn.tsx'
import { ErrorText } from '../components/ErrorText.tsx'
import { GuardedPage } from '../components/GuardedPage.tsx'
import { Heart } from '../components/Heart.tsx'
import { HelperStrip } from '../components/HelperStrip.tsx'
import { IconButton } from '../components/IconButton.tsx'
import { MarkdownField } from '../components/MarkdownField.tsx'
import { NoBurn } from '../components/NoBurn.tsx'
import { Refreshing } from '../components/Refreshing.tsx'
import { TheirVersion } from '../components/TheirVersion.tsx'
import { stillUploading } from '../image-upload.ts'
import { joinFirst, joinLink } from '../joining.ts'
import { useAction, useLoad } from '../load.ts'
import { renderMarkdown } from '../markdown.ts'
import { mealTally } from '../meal-tally.ts'
import { useViewer } from '../viewer.tsx'

export type MealsApi = Pick<
  ApiClient,
  | 'getMeals'
  | 'getEventAttendees'
  | 'getEventPantry'
  | 'heartPantryItem'
  | 'unheartPantryItem'
  | 'setMealLead'
  | 'joinMealCrew'
  | 'leaveMealCrew'
  | 'setMealIdea'
  | 'updateMealIntro'
  | 'uploadImage'
>

type Person = EventAttendeesResponse['attendees'][number]

type Plan =
  | (MealsResponse & { eventId: string; attendees: readonly Person[]; pantry: readonly EventPantryItem[] })
  | null

const wantedSections: readonly { kind: PantryKind; heading: string }[] = [
  { kind: 'breakfast', heading: 'What do you want for breakfast?' },
  { kind: 'snack', heading: 'Snacks' },
  { kind: 'household', heading: 'Around the house' },
]

export const Meals = ({ api }: { api: MealsApi }) => {
  const viewer = useViewer()
  const burn = useSelectedBurn()
  const [editingIntro, setEditingIntro] = useState(false)

  const { loaded, refreshing, reload } = useLoad<Plan>(
    async (signal) => {
      if (burn === undefined) return null

      const [plan, attendees, pantry] = await Promise.all([
        api.getMeals(burn.event.id, signal),
        api.getEventAttendees(burn.event.id, signal),
        api.getEventPantry(burn.event.id, signal),
      ])

      return {
        ...plan,
        eventId: burn.event.id,
        attendees: attendees.attendees,
        pantry: pantry.items,
      }
    },
    { key: burn?.event.id ?? '', fallback: 'Could not load the meal plan.', live: true, remember: 'meals' },
  )

  const { busy, error, failure, run } = useAction(reload)
  const plan = loaded.status === 'ready' ? loaded.data : null

  return (
    <GuardedPage title="Meals" require="approved">
      <h1>
        Meals <Refreshing on={refreshing} />
      </h1>

      {plan !== null && (
        <p class="row">
          <a href={shoppingPage(plan.eventId)}>Shopping list</a>
        </p>
      )}

      <ErrorText message={error} link={joinLink(error)} />

      {loaded.status === 'loading' && <p class="form-note">Loading…</p>}

      {loaded.status === 'failed' && <ErrorText message={loaded.message} />}

      {loaded.status === 'ready' && plan === null && <NoBurn absent="there is no meal plan to draw" />}

      {plan !== null && (
        <>
          {editingIntro ? (
            <IntroEditor
              intro={plan.intro_markdown}
              busy={busy}
              failure={failure}
              upload={api.uploadImage}
              onCancel={() => setEditingIntro(false)}
              onSave={(meal_intro_markdown) =>
                run(async () => {
                  await api.updateMealIntro(plan.eventId, { meal_intro_markdown })
                  setEditingIntro(false)
                }, 'Could not save that.')
              }
            />
          ) : (
            <div class="meal-intro">
              {plan.intro_markdown.trim() === '' ? (
                <p class="form-note">Nothing said here yet.</p>
              ) : (
                <div
                  class="markdown-preview"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(plan.intro_markdown) }}
                />
              )}
              <IconButton
                icon="edit"
                label="Edit these words"
                disabled={busy}
                onClick={() => setEditingIntro(true)}
              />
            </div>
          )}

          {plan.meals.length === 0 ? (
            <p class="form-note">
              {plan.slots.length === 0
                ? 'Nobody has set up meal times for this burn yet. Admins do that under Events.'
                : 'The meal times are set, but the days have not been filled in yet. Admins do that under Events.'}
            </p>
          ) : (
            <MealTable
              meals={plan.meals}
              eventId={plan.eventId}
              attendees={plan.attendees}
              viewerId={viewer.account?.id}
              busy={busy}
              onLead={(id, accountId) =>
                run(
                  () => api.setMealLead(id, { account_id: accountId }),
                  joinFirst('Could not save that.', accountId === viewer.account?.id ? 'mine' : 'theirs'),
                )
              }
              onStand={(id, role, joining, accountId) =>
                run(
                  () =>
                    joining
                      ? api.joinMealCrew(id, role, { account_id: accountId })
                      : api.leaveMealCrew(id, role, accountId),
                  joinFirst('Could not save that.', accountId === viewer.account?.id ? 'mine' : 'theirs'),
                )
              }
              onIdea={(id, food_idea) =>
                run(() => api.setMealIdea(id, { food_idea }), 'Could not save that.')
              }
            />
          )}

          <Wanted
            items={plan.pantry}
            eventId={plan.eventId}
            busy={busy}
            onHeart={(itemId, hearting) =>
              run(
                () =>
                  hearting
                    ? api.heartPantryItem(plan.eventId, itemId)
                    : api.unheartPantryItem(plan.eventId, itemId),
                joinFirst('Could not save that.'),
              )
            }
          />
        </>
      )}
    </GuardedPage>
  )
}

const IntroEditor = ({
  intro,
  busy,
  failure,
  upload,
  onSave,
  onCancel,
}: {
  intro: string
  busy: boolean
  upload: UploadImage
  failure?: unknown
  onSave: (intro: string) => void
  onCancel: () => void
}) => {
  const [draft, setDraft] = useState(intro)

  return (
    <div class="meal-intro">
      <MarkdownField
        label="What everyone should know"
        value={draft}
        maxLength={MAX_WELCOME_LENGTH}
        upload={upload}
        onInput={setDraft}
      />
      <TheirVersion failure={failure} at={['intro_markdown']} />
      <p class="row">
        <button type="button" disabled={busy || stillUploading(draft)} onClick={() => onSave(draft)}>
          Save
        </button>
        <button type="button" class="link-button" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </p>
    </div>
  )
}

const MealTable = ({
  meals,
  eventId,
  attendees,
  viewerId,
  busy,
  onLead,
  onStand,
  onIdea,
}: {
  meals: readonly Meal[]
  eventId: string
  attendees: readonly Person[]
  viewerId: string | undefined
  busy: boolean
  onLead: (id: string, accountId: string | null) => void
  onStand: (id: string, role: 'cleanup' | 'helper', joining: boolean, accountId: string) => void
  onIdea: (id: string, idea: string) => void
}) => {
  const tally = mealTally(meals)

  return (
    <div class="meal-table-wrap">
      <table class="meal-table">
        <thead>
          <tr>
            <th scope="col">Meal</th>
            <th scope="col">Food</th>
            <th scope="col">Lead</th>
            <th scope="col">Help</th>
            <th scope="col">Cleanup</th>
          </tr>
        </thead>
        <tbody>
          {meals.map((meal, index) => (
            <tr key={meal.id}>
              <th scope="row">
                {meals[index - 1]?.date === meal.date ? null : (
                  <>
                    {dayName(meal.date, 'short')}
                    <br />
                  </>
                )}
                <span class="meal-at">{meal.at}</span>
              </th>
              <td>
                <div>{meal.label}</div>
                <FoodIdea meal={meal} busy={busy} onIdea={onIdea} />
                {meal.kind !== 'chore' && (
                  <a class="form-note" href={schedulePage(eventId, { meal: meal.id })}>
                    {meal.ingredients.length === 0
                      ? 'Ingredients'
                      : `Ingredients (${meal.ingredients.length})`}
                  </a>
                )}
              </td>
              <td>
                {meal.kind === 'chore' && meal.lead === null ? (
                  <span class="form-note">—</span>
                ) : (
                  <HelperStrip
                    label={`${meal.label} on ${meal.date}`}
                    people={meal.lead === null ? [] : [meal.lead]}
                    max={1}
                    candidates={attendees}
                    shut={meal.kind === 'chore'}
                    everyone={attendees}
                    viewerId={viewerId}
                    busy={busy}
                    tally={tally}
                    onAdd={(accountId) => onLead(meal.id, accountId)}
                    onRemove={() => onLead(meal.id, null)}
                  />
                )}
              </td>
              {meal.kind === 'chore' && meal.helpers.length === 0 ? (
                <td>
                  <span class="form-note">—</span>
                </td>
              ) : (
                <Crew
                  meal={meal}
                  role="helper"
                  attendees={attendees}
                  viewerId={viewerId}
                  busy={busy}
                  joinable={meal.kind !== 'chore'}
                  tally={tally}
                  onStand={onStand}
                />
              )}
              <Crew
                meal={meal}
                role="cleanup"
                attendees={attendees}
                viewerId={viewerId}
                busy={busy}
                joinable
                tally={tally}
                onStand={onStand}
              />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const FoodIdea = ({
  meal,
  busy,
  onIdea,
}: {
  meal: Meal
  busy: boolean
  onIdea: (id: string, idea: string) => void
}) => {
  const [draft, setDraft] = useState(meal.food_idea)

  if (meal.kind === 'chore') return <span class="form-note">—</span>

  return (
    <input
      type="text"
      class="meal-idea"
      maxLength={MAX_OPTION_LABEL}
      placeholder="What's cooking?"
      aria-label={`Food idea for ${meal.label} on ${meal.date}`}
      disabled={busy}
      value={draft}
      onInput={(inputEvent) => setDraft(inputEvent.currentTarget.value)}
      onBlur={() => {
        if (draft !== meal.food_idea) onIdea(meal.id, draft)
      }}
    />
  )
}

const Crew = ({
  meal,
  role,
  attendees,
  viewerId,
  busy,
  joinable,
  tally,
  onStand,
}: {
  meal: Meal
  role: 'cleanup' | 'helper'
  attendees: readonly Person[]
  viewerId: string | undefined
  busy: boolean
  joinable: boolean
  tally: (accountId: string) => string
  onStand: (id: string, role: 'cleanup' | 'helper', joining: boolean, accountId: string) => void
}) => {
  const crew = role === 'helper' ? meal.helpers : meal.cleanup

  const offerable = attendees.filter((who) => role === 'cleanup' || who.account_id !== meal.lead?.account_id)

  return (
    <td>
      <HelperStrip
        label={`${role === 'helper' ? 'cooking' : 'cleanup'} at ${meal.label} on ${meal.date}`}
        people={crew}
        candidates={offerable}
        shut={!joinable}
        everyone={attendees}
        viewerId={viewerId}
        busy={busy}
        tally={tally}
        onAdd={(accountId) => onStand(meal.id, role, true, accountId)}
        onRemove={(accountId) => onStand(meal.id, role, false, accountId)}
      />
    </td>
  )
}

const Wanted = ({
  items,
  eventId,
  busy,
  onHeart,
}: {
  items: readonly EventPantryItem[]
  eventId: string
  busy: boolean
  onHeart: (itemId: string, hearting: boolean) => void
}) => (
  <>
    {wantedSections.map(({ kind, heading }) => (
      <section key={kind}>
        <h2>{heading}</h2>

        {kind === 'breakfast' && (
          <p class="form-note">A heart says you want it there. The one shopping sees how many.</p>
        )}

        <ul class="wanted-list">
          {items
            .filter((item) => item.kind === kind)
            .sort(byWanted)
            .map((item) => (
              <li key={item.id} class="wanted-row">
                <span>{item.name}</span>
                <Heart
                  what={item.name}
                  hearted={item.hearts.mine}
                  count={item.hearts.count}
                  people={item.hearts.people}
                  busy={busy}
                  onHeart={(hearting) => onHeart(item.id, hearting)}
                />
              </li>
            ))}
        </ul>
      </section>
    ))}

    <p class="form-note">
      Missing something? Ask for it on the <a href={bringPage(eventId)}>bring list</a>, or tell an admin and
      it joins the <a href={pantryPage()}>pantry</a>.
    </p>
  </>
)
